import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import {
    fetchImportMethod,
    modeFromMethod,
    persistImportMethod,
    ImportMode,
} from './Services/importMethodService';

type ProcessingModeSelectorProps = {
    channelKey?: string;
    /** The selected Data Type (feed type), e.g. "products". Mode is scoped per channel + feed type. */
    feedType: string;
    /** RBAC gate - whether the user may change the processing mode. */
    canEdit: boolean;
    blockedReason?: string;
    /** Reports the active mode up to the drawer (optimistic - fires before the save resolves). */
    onModeChange?: (mode: ImportMode | null) => void;
    /** Fired only after a successful save when the chosen mode is Scheduled, so the
     *  drawer can open the schedule configuration window. */
    onScheduleRequested?: () => void;
    id?: string;
};

type ModeOption = {
    mode: ImportMode;
    label: string;
    description: string;
    icon: React.ReactNode;
};

const BoltIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8Z" stroke="currentColor" strokeWidth="1.6"
            strokeLinejoin="round" />
    </svg>
);

const CalendarIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3.5" y="5" width="17" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" stroke="currentColor" strokeWidth="1.6"
            strokeLinecap="round" />
    </svg>
);

const PauseIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
        <path d="M10 9v6M14 9v6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
);

const InfoIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
        <path d="M12 11v5M12 8h.01" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
);

const InboxIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M3.5 13.5 6 6.5A2 2 0 0 1 7.9 5h8.2A2 2 0 0 1 18 6.5l2.5 7M3.5 13.5V18a2 2 0 0 0 2 2h13a2 2 0 0 0 2-2v-4.5M3.5 13.5H9a1 1 0 0 1 1 1 2 2 0 0 0 4 0 1 1 0 0 1 1-1h4.5"
            stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
);

const ClockIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
        <path d="M12 7.5V12l3 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
            strokeLinejoin="round" />
    </svg>
);

const MODE_OPTIONS: ModeOption[] = [
    { mode: 'immediate', label: 'Immediate', description: 'Process feeds as soon as they arrive', icon: <BoltIcon /> },
    { mode: 'scheduled', label: 'Scheduled', description: 'Run on a set date, time & cadence', icon: <CalendarIcon /> },
    { mode: 'manual', label: 'Off', description: 'Pause all processing for this connector', icon: <PauseIcon /> },
];

// Longer explanation shown once a mode is selected.
const MODE_DETAIL: Record<ImportMode, string> = {
    immediate:
        'Feeds are processed as soon as they arrive. Once one feed finishes, the system automatically checks for and picks up the next one, so there is no waiting between feeds.',
    scheduled:
        'Feeds are processed at a set date, time, and cadence you define. Files that arrive between scheduled runs are held until the next run.',
    manual:
        'Automatic processing is turned off for this connector. Feeds must be processed manually. Incoming files are held in the source location, untouched, and your Immediate and Scheduled settings are saved. Any in-progress imports finish normally.',
};

export const ProcessingModeSelector = ({
    channelKey,
    feedType,
    canEdit,
    blockedReason,
    onModeChange,
    onScheduleRequested,
    id = 'processing-mode-selector',
}: ProcessingModeSelectorProps) => {
    const [selectedMode, setSelectedMode] = useState<ImportMode | null>(null);
    const [methodId, setMethodId] = useState<string | undefined>(undefined);
    const [enqueuedJobs, setEnqueuedJobs] = useState<number>(0);
    const [lastConnectorTrigger, setLastConnectorTrigger] = useState<string | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);

    const applyMode = useCallback((mode: ImportMode | null) => {
        setSelectedMode(mode);
        onModeChange?.(mode);
    }, [onModeChange]);

    // Mode is scoped per channel + feed type - (re)fetch whenever the feed type changes.
    useEffect(() => {
        let cancelled = false;
        if (!channelKey || !feedType) {
            applyMode(null);
            return;
        }
        setLoading(true);
        fetchImportMethod(channelKey, feedType).then((result) => {
            if (cancelled) return;
            setLoading(false);
            const method = result.success ? result.data : undefined;
            setMethodId(method?.id);
            setEnqueuedJobs(method?.enqueued_jobs ?? 0);
            setLastConnectorTrigger(method?.last_connector_trigger);
            // When there is no saved method yet (empty response) default the display
            // to Off ("manual"). This is FE-only - we do NOT persist it (no API call);
            // an upsert only fires once the user actively picks a mode.
            applyMode(modeFromMethod(method) ?? 'manual');
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelKey, feedType]);

    const handleSelect = async (mode: ImportMode) => {
        if (saving) return;
        if (blockedReason) {
            toast.error(blockedReason);
            return;
        }
        if (!canEdit || mode === selectedMode) return;
        if (!channelKey || !feedType) return;

        const previousMode = selectedMode;
        applyMode(mode); // optimistic
        setSaving(true);

        const result = await persistImportMethod(channelKey, feedType, mode, methodId);
        setSaving(false);

        if (result.success) {
            if (result.data?.id) setMethodId(result.data.id);
            if (typeof result.data?.enqueued_jobs === 'number') setEnqueuedJobs(result.data.enqueued_jobs);
            toast.success('Feed processing mode updated');
            // Only after the mode is saved do we open the schedule configuration window.
            if (mode === 'scheduled') onScheduleRequested?.();
        } else {
            applyMode(previousMode); // revert on failure
            toast.error(result.error ?? 'Failed to update feed processing mode');
        }
    };

    const isOff = selectedMode === 'manual';
    const blocked = !!blockedReason;

    return (
        <div
            className="tw-rounded-lg tw-border tw-border-edge tw-p-4"
            data-testid={id}
        >
            <div className="tw-flex tw-items-center tw-justify-between">
                <span className="tw-text-base tw-font-semibold tw-text-ink">Feed Processing</span>
                {isOff && (
                    <span
                        className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-tone-warning-tint tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-tone-warning-ink"
                        data-testid={`${id}-status-chip`}
                    >
                        <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-tone-warning-ink" />
                        Processing Paused
                    </span>
                )}
            </div>
            <p className="tw-mt-1 tw-mb-3 tw-text-sm tw-text-ink-secondary">
                Choose how this connector runs. Only one option can be active at a time.
            </p>

            <div className="tw-flex tw-flex-col tw-gap-2" role="radiogroup" aria-label="Feed processing mode">
                {MODE_OPTIONS.map((opt) => {
                    const active = selectedMode === opt.mode;
                    const activeOff = active && opt.mode === 'manual';
                    const activeOn = active && opt.mode !== 'manual';
                    // Only Immediate surfaces the last connector trigger, and only while active.
                    const showLastTrigger = activeOn && opt.mode === 'immediate' && !!lastConnectorTrigger;
                    const cardClasses = [
                        'tw-flex tw-flex-col tw-gap-3 tw-rounded-lg tw-border tw-px-4 tw-py-3 tw-text-left tw-transition-colors',
                        activeOff ? 'tw-border-tone-warning-ink tw-bg-tone-warning-tint' : '',
                        activeOn ? 'tw-border-accent tw-bg-accent-tint' : '',
                        !active ? 'tw-border-edge hover:tw-border-edge-strong' : '',
                        !canEdit || blocked ? 'tw-opacity-60 tw-cursor-not-allowed' : 'tw-cursor-pointer',
                    ].join(' ');
                    return (
                        <button
                            key={opt.mode}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            aria-disabled={!canEdit || saving || blocked}
                            title={blockedReason}
                            disabled={!canEdit || saving}
                            className={cardClasses}
                            onClick={() => handleSelect(opt.mode)}
                            data-testid={`${id}-option-${opt.mode}`}
                        >
                            <span className="tw-flex tw-w-full tw-items-center tw-gap-3">
                                <span className={`tw-h-4 tw-w-4 tw-flex-shrink-0 tw-rounded-full tw-border-2 tw-flex tw-items-center tw-justify-center ${active ? (activeOff ? 'tw-border-tone-warning-ink' : 'tw-border-accent') : 'tw-border-edge-strong'}`}>
                                    {active && <span className={`tw-h-2 tw-w-2 tw-rounded-full ${activeOff ? 'tw-bg-tone-warning-ink' : 'tw-bg-accent'}`} />}
                                </span>
                                <span className={`tw-flex-shrink-0 ${active ? (activeOff ? 'tw-text-tone-warning-ink' : 'tw-text-accent') : 'tw-text-ink-tertiary'}`}>
                                    {opt.icon}
                                </span>
                                <span className="tw-flex tw-flex-col">
                                    <span className="tw-text-sm tw-font-semibold tw-text-ink">{opt.label}</span>
                                    <span className="tw-text-xs tw-text-ink-secondary">{opt.description}</span>
                                </span>
                            </span>
                            {showLastTrigger && (
                                <span
                                    className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-md tw-bg-surface tw-px-3 tw-py-2 tw-text-xs tw-text-ink-secondary"
                                    data-testid={`${id}-last-trigger`}
                                >
                                    <span className="tw-flex-shrink-0 tw-text-ink-tertiary"><ClockIcon /></span>
                                    <span>
                                        Last connector trigger{' '}
                                        <span className="tw-font-semibold tw-text-ink">{lastConnectorTrigger}</span>
                                    </span>
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {blocked && (
                <div
                    className="tw-mt-3 tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-bg-tone-warning-tint tw-p-3 tw-text-xs tw-text-tone-warning-ink"
                    role="alert"
                    data-testid={`${id}-blocked-reason`}
                >
                    <span className="tw-flex-shrink-0 tw-mt-px tw-text-tone-warning-ink"><InfoIcon /></span>
                    <span>{blockedReason}</span>
                </div>
            )}

            {isOff && !blocked && (
                <div
                    className="tw-mt-3 tw-rounded-lg tw-bg-tone-warning-tint tw-p-3"
                    data-testid={`${id}-paused-detail`}
                >
                    <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-tone-warning-ink">
                        <PauseIcon />
                        Processing Paused
                    </div>
                    <p className="tw-mt-1 tw-mb-0 tw-text-xs tw-text-tone-warning-ink">
                        {MODE_DETAIL.manual}
                    </p>
                </div>
            )}

            {selectedMode && !isOff && !blocked && (
                <div
                    className="tw-mt-3 tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-bg-surface-sunken tw-p-3 tw-text-xs tw-text-ink-secondary"
                    data-testid={`${id}-detail`}
                >
                    <span className="tw-flex-shrink-0 tw-mt-px tw-text-ink-tertiary"><InfoIcon /></span>
                    <span>{MODE_DETAIL[selectedMode]}</span>
                </div>
            )}

            {enqueuedJobs > 0 && (
                <div
                    className="tw-mt-3 tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-bg-surface-sunken tw-px-3 tw-py-2 tw-text-sm tw-text-ink-secondary"
                    data-testid={`${id}-pending-count`}
                >
                    <span className="tw-flex-shrink-0 tw-text-ink-tertiary"><InboxIcon /></span>
                    <span>
                        {enqueuedJobs} {enqueuedJobs === 1 ? 'feed is' : 'feeds are'} waiting
                        {isOff ? ' — processing is paused. Re-enable a mode to process them.' : '.'}
                    </span>
                </div>
            )}

            {loading && (
                <p className="tw-mt-2 tw-mb-0 tw-text-xs tw-text-ink-tertiary" data-testid={`${id}-loading`}>
                    Loading current mode…
                </p>
            )}
        </div>
    );
};

export default ProcessingModeSelector;
